import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { InputField, Button, LogoBloomays, Title, SingleLineText } from '@bloomays-lib/ui.shared';
import { SEND_TALENT_BOARD_MAGIC_LINK } from '@bloomays-lib/adapter.api-talent';
import { validateEmail } from '@bloomays-lib/utils.shared';
import { Logger } from '../../services/service-logger';
import mainsIllustration from '../../assets/illustrations/mains.jpg';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';
import { TalentBoardEventType } from '@bloomays-lib/types.shared';

const logger = Logger('AskMagicLink');

type AlertVariant = 'error' | 'success';

const AskMagicLink = (): React.ReactNode => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sendMagicLink, { loading: isSubmitting }] = useMutation(SEND_TALENT_BOARD_MAGIC_LINK);
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const { t } = useTranslation(['talentBoard', 'random']);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    logger.info('Traffic', {
      page: 'AskMagicLink',
      data: { email },
      evt: { name: TalentBoardEventType.TalentboardRequestMagicLink },
    });
    event.preventDefault();
    if (!email) return;
    if (!validateEmail(email.trim())) {
      return;
    }
    setShowErrorCard(false);
    setShowSuccessCard(false);

    try {
      const normalized = email.trim().toLowerCase();
      await sendMagicLink({
        variables: { email: normalized },
        context: { clientName: 'api.talents', isPublic: true },
      });
      setShowSuccessCard(true);
    } catch (error) {
      logger.error('Error sending talent board magic link', { email }, error as Error);
      monitorException(error, { name: 'AskMagicLink', email });
      setShowErrorCard(true);
    }
  };

  const handleBackClick = () => {
    setShowErrorCard(false);
    setShowSuccessCard(false);
    setEmail('');
  };

  const renderForm = () => (
    <StyledForm onSubmit={handleSubmit}>
      <StyledInputField
        id="email-address"
        name="email"
        value={email}
        type="email"
        label={t('mail', { ns: 'random' })}
        placeholder="john@gmail.com"
        handleChange={handleEmailChange}
        helperText={t('loginInstruction')}
        required
      />
      <FormActions>
        <Grid container spacing={2} justifyContent="space-between" width="100%">
          <BackButton type="button" onClick={() => navigate('/')} aria-label={t('random:back')}>
            <BackArrow />
          </BackButton>
          <SubmitButton
            textButton={t('magickLinkButtonLabel')}
            buttonType="submit"
            disable={!validateEmail(email.trim()) || isSubmitting}
            loading={isSubmitting}
            color="primary"
            size="medium"
          />
        </Grid>
      </FormActions>
    </StyledForm>
  );

  const renderAlert = (variant: AlertVariant, message: string) => (
    <AlertSection>
      <AlertContent>
        {variant === 'error' ? (
          <ErrorIcon>
            <svg width="69" height="70" viewBox="0 0 69 70" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M62.1004 35.0004C62.1004 50.2434 49.7434 62.6004 34.5004 62.6004C19.2573 62.6004 6.90039 50.2434 6.90039 35.0004C6.90039 19.7573 19.2573 7.40039 34.5004 7.40039C49.7434 7.40039 62.1004 19.7573 62.1004 35.0004ZM37.9504 48.8004C37.9504 50.7058 36.4058 52.2504 34.5004 52.2504C32.595 52.2504 31.0504 50.7058 31.0504 48.8004C31.0504 46.895 32.595 45.3504 34.5004 45.3504C36.4058 45.3504 37.9504 46.895 37.9504 48.8004ZM34.5004 17.7504C32.595 17.7504 31.0504 19.295 31.0504 21.2004V35.0004C31.0504 36.9058 32.595 38.4504 34.5004 38.4504C36.4058 38.4504 37.9504 36.9058 37.9504 35.0004V21.2004C37.9504 19.295 36.4058 17.7504 34.5004 17.7504Z"
                fill="currentColor"
              />
            </svg>
          </ErrorIcon>
        ) : (
          <SuccessIcon>
            <svg width="69" height="70" viewBox="0 0 69 70" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M34.5004 62.6004C49.7435 62.6004 62.1004 50.2435 62.1004 35.0004C62.1004 19.7573 49.7435 7.40039 34.5004 7.40039C19.2573 7.40039 6.90039 19.7573 6.90039 35.0004C6.90039 50.2435 19.2573 62.6004 34.5004 62.6004ZM47.2899 30.5399C48.6372 29.1926 48.6372 27.0082 47.2899 25.6609C45.9426 24.3136 43.7582 24.3136 42.4109 25.6609L31.0504 37.0214L26.5899 32.5609C25.2426 31.2136 23.0582 31.2136 21.7109 32.5609C20.3636 33.9082 20.3636 36.0926 21.7109 37.4399L28.6109 44.3399C29.9582 45.6872 32.1426 45.6872 33.4899 44.3399L47.2899 30.5399Z"
                fill="currentColor"
              />
            </svg>
          </SuccessIcon>
        )}
        <AlertMessage variant={variant} dangerouslySetInnerHTML={{ __html: message }} />
      </AlertContent>
      <BackActions>
        <BackButton type="button" onClick={handleBackClick} aria-label={t('random:back')}>
          <BackArrow />
        </BackButton>
      </BackActions>
    </AlertSection>
  );

  const shouldShowForm = !showErrorCard && !showSuccessCard;

  return (
    <PageWrapper>
      <IllustrationColumn>
        <LogoContainer>
          <LogoBloomays alt="Bloomays" onClick={() => navigate('/')} />
        </LogoContainer>
        <LogoContainerMobile>
          <LogoBloomays alt="Bloomays" onClick={() => navigate('/')} />
        </LogoContainerMobile>
      </IllustrationColumn>
      <ContentContainer>
        <LoginCard>
          <Title textTitle={t('homeTitle')} sx={{ textAlign: 'center' }} />
          {shouldShowForm && <DescriptionText text={t('loginMessage')} variant="body1Regular" />}
          {shouldShowForm && renderForm()}
          {!shouldShowForm && showErrorCard && renderAlert('error', t('unknownEmailErrorMessage'))}
          {!shouldShowForm && showSuccessCard && renderAlert('success', t('magickLinkSuccessMessage'))}
        </LoginCard>
      </ContentContainer>
    </PageWrapper>
  );
};

export default AskMagicLink;
export { AskMagicLink };

const PageWrapper = styled('section')(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#3B7FF5',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
}));

const IllustrationColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '800px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
  background: `url("${mainsIllustration}") lightgray 50% / cover no-repeat`,
  position: 'relative',

  '&::after': {
    content: '""',
    width: '72px',
    height: '100%',
    background: 'linear-gradient(270deg, #3B7FF5 0%, rgba(59, 127, 245, 0.00) 77.4%)',
    position: 'absolute',
    right: 0,
    top: 0,
  },

  [theme.breakpoints.down('xl')]: {
    width: '40%',
  },

  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: '300px',
    alignSelf: 'stretch',
    position: 'relative',

    '&::after': {
      background: 'linear-gradient(180deg, rgba(59, 127, 245, 0.00) 0%, #3B7FF5 40%)',
      width: '100%',
      height: '50px',
      bottom: 0,
      top: 'auto',
      right: 'auto',
    },
  },
}));

const LogoContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '52px',
  top: '40px',
  width: '213.872px',
  height: '31px',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const LogoContainerMobile = styled('div')(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.down('md')]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: '20%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '160px',
    height: '31px',
    zIndex: 20,
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  gap: theme.spacing(4),
  width: '50%',
  margin: '0 auto',

  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const LoginCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56%',
  margin: '0 auto',
  borderRadius: '15px',
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(4),
  gap: theme.spacing(3),
  boxShadow: '0 25px 70px rgba(33, 33, 33, 0.12)',

  [theme.breakpoints.down('xl')]: {
    width: '70%',
    padding: theme.spacing(1),
    gap: theme.spacing(2),
  },

  [theme.breakpoints.down('lg')]: {
    width: '90%',
  },
}));

const StyledForm = styled('form')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledInputField = styled(InputField)(({ theme }) => ({
  width: '100%',

  // Fond blanc pour le champ de saisie
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.neutral.white,
    borderRadius: '8px',

    // Couleur de bordure au focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.red[60],
      borderWidth: '2px',
    },

    // Fond blanc maintenu au focus
    '&.Mui-focused': {
      backgroundColor: theme.palette.neutral.white,
    },
  },

  // Personnalisation du label (inspiré de l'exemple MUI)
  '& label.Mui-focused': {
    color: `${theme.palette.red[60]} !important`,
  },

  // Cibler aussi l'astérisque
  '& .MuiFormLabel-asterisk': {
    color: `${theme.palette.red[60]} !important`,
  },

  // Helper text avec fond transparent
  '& .MuiFormHelperText-root': {
    backgroundColor: 'transparent',
    margin: theme.spacing(1, 0, 0, 0),
    fontSize: '12px',
    color: theme.palette.neutral[50],
  },

  [theme.breakpoints.down('xl')]: {
    width: '90%',
  },
}));

const DescriptionText = styled(SingleLineText)(({ theme }) => ({
  textAlign: 'center',
  width: '90%',
  color: theme.palette.neutral.black,
  lineHeight: 1.2,
  fontSize: '14px',

  // Responsive pour mobile
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
    textAlign: 'center',
    padding: '0 10px',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    textAlign: 'center',
    padding: '0 10px',
  },
}));

const FormActions = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: 30,
  paddingLeft: 32,
  paddingRight: 32,
  backgroundImage: 'linear-gradient(90deg, #FF9D6A 0%, #FF6B97 100%)',
  color: '#212121',
  textTransform: 'none',
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #ffb083 0%, #ff7fa6 100%)',
  },

  [theme.breakpoints.down('md')]: {
    maxWidth: '80%',
    fontSize: '11px',
  },
}));

const AlertSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  alignSelf: 'stretch',
  width: '90%',
  margin: '0 auto',

  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const AlertContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  flex: 1,

  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(4),
  },
}));

const ErrorIcon = styled('div')(({ theme }) => ({
  width: '69px',
  height: '69px',
  flexShrink: 0,
  aspectRatio: '1/1',
  position: 'relative',
  color: theme.palette.red[80],

  // Responsive pour mobile
  [theme.breakpoints.down('md')]: {
    width: '56px',
    height: '56px',
  },

  [theme.breakpoints.down('sm')]: {
    width: '48px',
    height: '48px',
  },
}));

const SuccessIcon = styled('div')(({ theme }) => ({
  width: '69px',
  height: '69px',
  flexShrink: 0,
  aspectRatio: '1/1',
  position: 'relative',
  color: theme.palette.green[100],

  // Responsive pour mobile
  [theme.breakpoints.down('md')]: {
    width: '56px',
    height: '56px',
  },

  [theme.breakpoints.down('sm')]: {
    width: '48px',
    height: '48px',
  },
}));

const BackArrow = styled(ArrowBackIcon)(({ theme }) => ({
  color: theme.palette.neutral.black,
}));

const AlertMessage = styled('p')<{ variant: AlertVariant }>(({ variant, theme }) => ({
  margin: 0,
  fontFamily: '"Poppins", sans-serif',
  fontSize: 14,
  lineHeight: '19.6px',
  color: variant === 'error' ? theme.palette.red[80] : theme.palette.green[100],

  [theme.breakpoints.down('md')]: {
    fontSize: 12,
    lineHeight: '16.8px',
  },
}));

const BackActions = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-end',
}));

const BackButton = styled('button')(({ theme }) => ({
  width: 35,
  height: 35,
  borderRadius: 5,
  border: 'none',
  backgroundColor: theme.palette.neutral.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));
